import SyntaxHighlighter from "react-syntax-highlighter/dist/esm/default-highlight"; 
import { darcula } from "react-syntax-highlighter/dist/esm/styles/hljs";
import "./css/TextFormat.css";
import { DataState } from "../context/DataProvider";
import CopyButton from "./custom/CopyButton";
import AuthImage from "./chat/AuthImage";


const renderFormattedText = (text, sources) => {
  const { theme } = DataState();
  // Regex to capture URLs, bold, italic, citations, and tables
  const regexPatterns = {
    image: /!\[([^\]]+)\]\((https?:\/\/[^\s\)]+)\)/g,
    link: /\[([^\]]+)\]\((https?:\/\/[^\s\)]+)\)/g,
    bold: /\*\*([^\*]+)\*\*/g,
    italic: /_([^_]+)_/g,
    citation: /\[citation:(\d+)\]/g,
    table: /\|(.+?)\|/g, // Simple table row matcher
  };

  const elements = [];
  let lastIndex = 0;

  const addElement = (type, match, index) => {
    const beforeText = text.substring(lastIndex, match.index);
    if (beforeText) {
      elements.push(
        <span key={`text-${index}`}>
          {renderFormattedText(beforeText, sources)}
        </span>
      );
    }

    switch (type) {
      case "image":
        elements.push(
          // <img
          //   key={`img-${index}`}
          //   src={match[2]}
          //   alt={match[1]}
          //   style={{ maxWidth: '100%', height: 'auto' }}
          // />
          <AuthImage
            key={`img-${index}`}
            src={match[2]}
            alt={match[1]}
            className="max-w-full h-auto"
          />
        );
        break;
      case "link":
        elements.push(
          <a
            key={`link-${index}`}
            href={match[2]}
            target="_blank"
            className="text-[#5652CF] hover:underline font-sans"
          >
            {match[1]}
          </a>
        );
        break;
      case "citation":
        const citationIndex = parseInt(match[1], 10) - 1;
        const source = sources[citationIndex];
        break;
      case "bold":
        elements.push(
          <strong key={`bold-${index}`}>
            {renderFormattedText(match[1], sources)}
          </strong>
        );
        break;
      case "italic":
        elements.push(
          <em key={`italic-${index}`}>
            {renderFormattedText(match[1], sources)}
          </em>
        );
        break;
      case "table":
        elements.push(renderTable(text, sources));
        lastIndex = text.length; // Skip remaining text as table rows are handled separately
        break;
      default:
        break;
    }

    lastIndex = match.index + match[0].length;
  };

  const patterns = Object.entries(regexPatterns);
  let matchFound = true;

  while (matchFound) {
    matchFound = false;
    let firstMatch = null;
    let firstMatchType = null;

    patterns.forEach(([type, pattern]) => {
      pattern.lastIndex = lastIndex;
      const match = pattern.exec(text);
      if (match && (!firstMatch || match.index < firstMatch.index)) {
        firstMatch = match;
        firstMatchType = type;
      }
    });

    if (firstMatch) {
      addElement(firstMatchType, firstMatch, elements.length);
      matchFound = true;
    }
  }

  const remainingText = text.substring(lastIndex);
  if (remainingText) {
    elements.push(<span key={`text-end`}>{remainingText}</span>);
  }

  return elements;
};

const FormattedText = ({ text, sources }) => {
  const content = [];
  const lines = text.split("\n");

  let tableRows = [];
  let insideTable = false;

  lines.forEach((line, index) => {
    // Heading check
    const headingMatch = line.match(/^(#{1,6})\s(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const HeadingTag = `h${level}`;
      content.push(
        <HeadingTag key={index} className={`text-${3 - level}xl font-bold my-4`}>
          {renderFormattedText(headingMatch[2], sources)}
        </HeadingTag>
      );
      return;
    }

    if (line.startsWith("|")) {
      insideTable = true;
      tableRows.push(line);
    } else {
      if (insideTable) {
        content.push(renderTable(tableRows.join("\n"), sources));
        tableRows = [];
        insideTable = false;
      }

      const isListItem = line.startsWith("* ");
      if (isListItem) {
        content.push(
          <li key={index} className="ml-4 list-disc">
            {renderFormattedText(line.slice(2), sources)}
          </li>
        );
      } else {
        content.push(
          <p key={index} className="my-2">
            {renderFormattedText(line, sources)}
          </p>
        );
      }
    }
  });

  if (insideTable) {
    content.push(renderTable(tableRows.join("\n"), sources));
  }

  return <div className="formatted-text">{content}</div>;
};

const renderTable = (text, sources) => {
  const rows = text.split("\n").filter((row) => row.startsWith("|"));
  const tableRows = rows.map((row, index) => {
    const columns = row
      .split("|")
      .slice(1, -1)
      .map((col, colIndex) => (
        <td key={`col-${colIndex}`} className="border px-4 py-2">
          {renderFormattedText(col.trim(), sources)}
        </td>
      ));
    return <tr key={`row-${index}`}>{columns}</tr>;
  });

  return (
    <table
      key="table"
      className="table-auto border-collapse border br-1 w-full my-4"
    >
      <tbody>{tableRows}</tbody>
    </table>
  );
};

const TextFormat = ({ response, sources = [] }) => {
  const codeRegex = /```([a-zA-Z]+)?([\s\S]*?)```/g;
  const matches = Array.from(response.matchAll(codeRegex));

  let lastIndex = 0;
  const content = [];

  matches.forEach((match, index) => {
    const language = match[1];
    const code = match[2];

    content.push(
      <p key={`non-code-${index}`}>
        <FormattedText
          text={response.substring(lastIndex, match.index).trim()}
          sources={sources}
        />
      </p>
    );

    content.push(
      <div className="oneM-display-code font-sans overflow-hidden max-w-[40rem] whitespace-normal" key={`code-${index}`}>
        <div className="rounded-xl overflow-hidden text-sm relative">
          <span className="absolute right-3 top-3 bg-1 rounded-lg">
            <CopyButton copy={code} />
          </span>
          <SyntaxHighlighter
            language={language || "javascript"}
            style={darcula}
          >
            {code}
          </SyntaxHighlighter>
        </div>
      </div>
    );

    lastIndex = match.index + match[0].length;
  });

  if (lastIndex < response.length) {
    content.push(
      <p key={`non-code-${matches.length} text-s-text`}>
        <FormattedText
          text={response.substring(lastIndex).trim()}
          sources={sources}
        />
      </p>
    );
  }

  return <div className="oneM-display">{content}</div>;
};

export default TextFormat;
